import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';


import 'react-toastify/dist/ReactToastify.css';
import 'react-loading-skeleton/dist/skeleton.css'
import './setup/i18n';

import { default as routes } from 'src/routes/Routes';


import { authSelector, logOut, setCredentials } from './store/reducers/auth';
import { useRefreshMutation } from './store/api/auth/authApiSlice';
import { useGetCartQuery } from './store/api/cart/catsApiSlice';
import apiSlice from './store/api/apiSlice';

import useError from './hooks/useError';
import { AppPromoModal, LoadingPage } from './components';
import { isRTL } from './utils/global';
import { getStorageItem } from './utils/storage';

import { cartUpdateItems } from './store/reducers/cart';
import { mapServerCartItems, sanitizeStoredCartItems, setCartStorageEnabled, storageCartReset, storageSetCartItems } from './utils/cart';
import { MALEK_CART_ITEMS_KEY, MALEK_STORE_TOKEN_KEY } from './constants/storageKeys';
import { clearAuthStorage } from './utils/auth';
import { useGuestWishlistSync } from './hooks';


const App = () => {
  const auth = useSelector(authSelector);
  const [refresh, { isLoading, isError, error }] = useRefreshMutation();
  const dispatch = useDispatch();
  const { showError } = useError();

  useGuestWishlistSync();


  // Side Effects
  useEffect(() => {
    if (!auth.access) {
      const reAuthHandle = async () => {
        const refreshToken = await getStorageItem(MALEK_STORE_TOKEN_KEY);
        if (refreshToken) {
          const res = await refresh(refreshToken);
          if ('data' in res) {
            const { access } = res.data;
            const userData = {
              access: access,
              refresh: refreshToken
            }
            dispatch(setCredentials(userData));
          }
        } else {
          clearAuthStorage();
        }
      }
      reAuthHandle();
    }
    // Guest carts live in localStorage. For an authenticated user the server
    // cart is authoritative, so local storage must not seed Redux — otherwise a
    // stale local copy overwrites (and hides) changes made on another device.
    // The authenticated cart is hydrated by the `getCart` effect below.
    if (auth.access) {
      return;
    }

    const loadCartItems = () => {
      try {
        const stored = getStorageItem(MALEK_CART_ITEMS_KEY);
        const items = sanitizeStoredCartItems(stored);
        // Rewrite storage so a corrupted/legacy payload is repaired in place.
        if (Array.isArray(stored) && items.length !== stored.length) {
          storageSetCartItems(items);
        }
        dispatch(cartUpdateItems({ items }));
      } catch (err) {
        storageCartReset();
        dispatch(cartUpdateItems({ items: [] }));
      }
    }
    loadCartItems();
  }, [auth.access, dispatch]);

  // localStorage is guest persistence only; see utils/cart.ts.
  useEffect(() => {
    setCartStorageEnabled(!auth.access);
  }, [auth.access]);

  // Authenticated bootstrap: pull the canonical server cart and make it the
  // single source Redux renders from (badge, lines, quantities, totals).
  const { data: serverCart } = useGetCartQuery(undefined, {
    skip: !auth.access,
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
    refetchOnFocus: true,
  });

  useEffect(() => {
    if (!auth.access || !serverCart) {
      return;
    }

    dispatch(cartUpdateItems({ items: mapServerCartItems(serverCart.items) }));
  }, [auth.access, dispatch, serverCart]);

  useEffect(() => {
    if (isError) {
      if ((error as any).status === 500 || (error as any).status === "FETCH_ERROR") {
        showError(error);
      }
      if ((error as any).status === 401 || (error as any).status === 403) {
        dispatch(logOut());
        clearAuthStorage();
        dispatch(apiSlice.util.resetApiState());
      }
    }
  }, [dispatch, error, isError, showError]);

  if (isLoading) {
    return <LoadingPage />
  }


  return (
    <Router>
      <ToastContainer className={isRTL() ? "rtl_toast" : ""} stacked
        position={isRTL() ? 'top-right' : 'top-left'} />
      <AppPromoModal />
      <Routes>
        {routes.map((route, index) => (
          <Route key={`layout__${index}`} path={route.path} element={<route.element />}>
            {route.routes.map((nestedRoute, nestedIndex) => (
              <Route
                key={`route__${nestedIndex}`}
                path={nestedRoute.path}
                element={<nestedRoute.element />}
              />
            ))}
          </Route>
        ))}
      </Routes>
    </Router>);
}



export default App;
